import * as React from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { RffFieldProps } from '../form/FieldProps';
import { ClassNameProp } from '../form/types';
import { Label } from '../label/Label';
import { FilterType } from './types';
import { Menu } from '../menu/Menu';
import { MenuItem } from '../menu/MenuItem';
import { MenuDivider } from '../menu/MenuDivider';
import { useField } from 'react-final-form';
import { Icon } from '../icon/Icon';
import {
  mcCheck,
  mcDropDownFilled,
  mcNoValueSpecified,
} from '@mc/react-ui-icons';
import { Popup } from '../popover/Popup';
import { Text } from '../typography/Text';
import { ExtendedFilter } from './Filters';

export const FilterItem: React.FC<
  React.PropsWithChildren<
    RffFieldProps &
      ClassNameProp & {
        label: React.ReactNode;
        disabled?: boolean;
        filterType?: FilterType;
        /**
         * Zda lze hodnotu filtrovat na vyplněnost/nevyplněnost, případně nerovná se apod.
         */
        extendedFilter?: ExtendedFilter;
      }
  >
> = ({ children, label, name, className, filterType, extendedFilter }) => {
  const showFilledFilterable =
    filterType === 'default' &&
    (extendedFilter?.filterByFilled || extendedFilter?.filterByNot);

  const cls = classNames(className, Classes.FILTER_GROUP, {
    [Classes.FILTER_FILLED_FILTERABLE]: showFilledFilterable,
  });

  const formField = useField(name);

  const isEmpty =
    filterType === 'default' ? formField.input.value?.emptyValue!! : false;
  const isNotEmpty =
    filterType === 'default' ? formField.input.value?.notEmptyValue!! : false;
  const isNot =
    filterType === 'default' ? formField.input.value?.notValue!! : false;

  const handleFilterByIsEmpty = () => {
    if (filterType === 'default') {
      formField.input.onChange({
        value: undefined,
        emptyValue: true,
        notEmptyValue: false,
        notValue: false,
      });
    }
  };

  const handleFilterByIsNotEmpty = () => {
    if (filterType === 'default') {
      formField.input.onChange({
        value: undefined,
        emptyValue: false,
        notEmptyValue: true,
        notValue: false,
      });
    }
  };

  const handleFilterByNotValue = () => {
    if (filterType === 'default') {
      formField.input.onChange({
        value: formField.input.value?.value,
        emptyValue: false,
        notEmptyValue: false,
        notValue: true,
      });
    }
  };

  const handleFilterByValue = () => {
    if (filterType === 'default') {
      formField.input.onChange({
        value: formField.input.value?.value,
        emptyValue: false,
        notEmptyValue: false,
        notValue: false,
      });
    }
  };

  return (
    <div className={cls}>
      {!showFilledFilterable && (
        <Label className={Classes.FILTER_LABEL}>{label}</Label>
      )}
      {showFilledFilterable && (
        <Label className={Classes.FILTER_LABEL}>
          <Popup
            trigger={
              <span className={Classes.FILTER_POPUP_LABEL}>
                {label}&nbsp;&nbsp;
                {isNot && (
                  <>
                    <Icon
                      className={Classes.FILTER_POPUP_LABEL_ALLWAYS_VISIBLE}
                      intent={'danger'}
                      icon={mcNoValueSpecified}
                    />
                    &nbsp;&nbsp;
                  </>
                )}
                <Icon icon={mcDropDownFilled} />
              </span>
            }
          >
            {(closePopup) => {
              return (
                <Menu>
                  <MenuItem
                    icon={
                      !isNotEmpty && !isEmpty && !isNot ? (
                        <Icon icon={mcCheck} />
                      ) : undefined
                    }
                    text={'Zadaná hodnota'}
                    onClick={() => {
                      closePopup();
                      handleFilterByValue();
                    }}
                  />
                  {extendedFilter?.filterByNot && (
                    <MenuItem
                      text={'Nerovná se zadané hodnotě'}
                      icon={isNot ? <Icon icon={mcCheck} /> : undefined}
                      onClick={() => {
                        closePopup();
                        handleFilterByNotValue();
                      }}
                    />
                  )}
                  {extendedFilter?.filterByFilled && (
                    <>
                      <MenuDivider />
                      <MenuItem
                        text={'Vyplněno'}
                        icon={isNotEmpty ? <Icon icon={mcCheck} /> : undefined}
                        onClick={() => {
                          closePopup();
                          handleFilterByIsNotEmpty();
                        }}
                      />
                      <MenuItem
                        text={'Nevyplněno'}
                        icon={isEmpty ? <Icon icon={mcCheck} /> : undefined}
                        onClick={() => {
                          closePopup();
                          handleFilterByIsEmpty();
                        }}
                      />
                    </>
                  )}
                </Menu>
              );
            }}
          </Popup>
        </Label>
      )}
      <div className={Classes.FILTER_FIELD}>
        {isEmpty && <Text type={'value'}>Nevyplněno</Text>}
        {isNotEmpty && <Text type={'value'}>Vyplněno</Text>}
        {!isEmpty && !isNotEmpty && children}
      </div>
    </div>
  );
};

FilterItem.displayName = 'FilterItem';
